import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { selectors } from 'darwin_core';
import { MODES } from 'darwin_core/utils/consts.js';

import { zonePath } from '../navigation/paths.js';
import { tileState } from './tileState.js';
import {
  formatClock12,
  getScheduleNowNext,
} from '../dashboard/formatSchedule.js';
import {
  ChevronRightIcon,
  ThermostatIcon,
  WaterIcon,
} from '../components/icons.jsx';

// A single Property zone tile — the app's Tile look (icon, a big prominent state, a
// small state line, the zone name, a chevron) reworked into a navigation target:
// pressing it opens the zone screen. While the zone's thermostat is in holiday the
// tile is a non-interactive card (matching the app, which blocks the press) —
// holiday is per thermostat, so in a two-zone home the other tile may still open;
// offline zones still open — the zone screen shows the last-reported detail.
//
// zoneId is the darwin_core tuple [prtId, endpointId]; every per-zone selector
// takes (state, homeId, zoneId) in that shape.
export default function ZoneTile({ homeId, zoneId }) {
  const isDhw = useSelector((s) => selectors.isDhw(s, homeId, zoneId));
  const online = useSelector((s) => selectors.isZoneOnline(s, homeId, zoneId));
  const inHoliday = useSelector((s) =>
    Boolean(selectors.isInHolidayMode(s, homeId, zoneId)),
  );
  const mode = useSelector((s) => selectors.getMode(s, homeId, zoneId));
  const setpoint = useSelector((s) => selectors.getSetpoint(s, homeId, zoneId));
  const minSetpoint = useSelector((s) =>
    selectors.getMinSetpoint(s, homeId, zoneId),
  );
  const roomTemperature = useSelector((s) =>
    selectors.getCurrentTemperature(s, homeId, zoneId),
  );
  // Hot water's big state is the live On/Off. getDhwActiveMode derives it from the
  // schedule and can throw on a zone that reported none (getSchedule ->
  // flattenSchedule([...undefined])), so read it defensively: a bad frame must
  // degrade the one tile, never blank the property (this renders under the app's
  // ErrorBoundary). Only DHW zones need it.
  const dhwActiveMode = useSelector((s) => {
    if (!isDhw) {
      return '';
    }
    try {
      return selectors.getDhwActiveMode(s, homeId, zoneId);
    } catch {
      return '';
    }
  });
  const name = useSelector((s) =>
    selectors.getPrtFriendlyName(s, homeId, zoneId),
  );
  // Raw reported schedule (stable store reference — no re-mapping selector), used
  // for the "Until <next change>" line. It is only meaningful while the zone is
  // following its schedule (Auto); in Manual/Off/override the current state does
  // not end at the next scheduled event, so the tile shows the mode instead. Read
  // via the override-free getScheduleNowNext, which sanitises a partial frame.
  const rawSchedule = useSelector((s) => {
    const home = s.homeList.homes.find((h) => h.id === homeId);
    const zone = home?.zones?.find(
      (z) => z.zoneId?.join('_') === zoneId.join('_'),
    );
    return zone?.schedule;
  });
  const nextStart =
    mode === MODES.Auto ? getScheduleNowNext(rawSchedule).nextStart : undefined;
  const until = typeof nextStart === 'number' ? formatClock12(nextStart) : null;

  const purpose = isDhw ? 'Hot water' : 'Heating';
  const heading = name || purpose;
  const { kind, bigState, smallState, active, frost, navigable } = tileState({
    isDhw,
    online,
    inHoliday,
    mode,
    setpoint,
    minSetpoint,
    roomTemperature,
    dhwActiveMode,
    until,
  });

  const Icon = kind === 'water' ? WaterIcon : ThermostatIcon;

  const className =
    `zone-tile-card${active ? ' zone-tile-card--active' : ''}` +
    `${online ? '' : ' zone-tile-card--offline'}`;

  // The label read by assistive tech leads with the name, then purpose + states,
  // so the announcement is meaningful regardless of the visual DOM order.
  const label = [heading, purpose, bigState, smallState]
    .filter(Boolean)
    .join(', ');

  const inner = (
    <>
      <div className="zone-tile-card__top">
        <span className="zone-tile-card__icon" aria-hidden="true">
          <Icon />
        </span>
        {/* The app colours the value only in two cases: frost (cold blue) and
            hot water On (brand green); otherwise it is plain white text. */}
        {bigState && (
          <span
            className={
              'zone-tile-card__big' +
              (frost ? ' zone-tile-card__big--frost' : '') +
              (kind === 'water' && active ? ' zone-tile-card__big--on' : '')
            }
          >
            {bigState}
          </span>
        )}
      </div>
      <div className="zone-tile-card__bottom">
        <span className="zone-tile-card__text">
          {smallState && (
            <span className="zone-tile-card__small">{smallState}</span>
          )}
          <span className="zone-tile-card__name">{heading}</span>
        </span>
        {navigable && (
          <span className="zone-tile-card__chevron" aria-hidden="true">
            <ChevronRightIcon />
          </span>
        )}
      </div>
    </>
  );

  if (!navigable) {
    // Non-interactive while the home is in holiday: still perceivable, but not a
    // link / tab stop.
    return (
      <div
        className={`${className} zone-tile-card--locked`}
        role="group"
        aria-label={`${label} (locked while in holiday)`}
      >
        {inner}
      </div>
    );
  }

  return (
    <Link
      to={zonePath(homeId, zoneId)}
      className={className}
      aria-label={label}
    >
      {inner}
    </Link>
  );
}
